import _ from 'lodash';
import constants from '../../../../config/constants';
import { generateDeliveryText } from '../../../../utils/hooks/useCurrentDeliveryText';
import { getSelectedData } from '../DeliveryOptions.Helper';
import { convertToNumber, getLocaleTime, pad } from '../utils';

const { MONTHS, AVAILABILITY_HOMEDELIVERY, AVAILABILITY_OUTOFSTOCK, AVAILABILITY_INSTOCK } = constants;

const getChosenSlot = (customTimeSlots, currentHour) => {
  let chosenSlot;

  // Si solo hay un slot, lo retornamos directamente
  if (customTimeSlots.length === 1) {
    [chosenSlot] = customTimeSlots;
    return chosenSlot;
  }

  if (customTimeSlots.length > 1) {
    const getStartHour = (timeRange = '') => {
      const matched = (timeRange || '').match(/[\d]{1,2}/);
      return matched ? Number(matched[0]) : null;
    };

    const parseCost = (cost) => {
      if (!cost) return null;
      const regex = /[\d.,]+/g;
      const match = (cost || '').match(regex);
      if (!match || match.length === 0) return null;
      return convertToNumber(match[0]);
    };

    const slotsWithCost = customTimeSlots.map((s) => ({
      slot: s,
      costValue: parseCost(s.cost),
    }));

    const availableCosts = slotsWithCost.map((s) => s.costValue).filter((v) => v !== null && !Number.isNaN(v));

    [chosenSlot] = customTimeSlots;

    if (availableCosts.length > 0) {
      const minCost = Math.min(...availableCosts);
      const minSlots = slotsWithCost.filter((s) => s.costValue === minCost).map((s) => s.slot);

      if (minSlots.length === 1) {
        [chosenSlot] = minSlots;
      } else {
        const futureCandidates = minSlots.filter((s) => {
          const start = getStartHour(s.timeRange);
          return start !== null && start >= currentHour;
        });

        if (futureCandidates.length > 0) {
          futureCandidates.sort((a, b) => getStartHour(a.timeRange) - getStartHour(b.timeRange));
          [chosenSlot] = futureCandidates;
        } else {
          minSlots.sort((a, b) => {
            const hourDistanceA = Math.abs((getStartHour(a.timeRange) || 0) - currentHour);
            const hourDistanceB = Math.abs((getStartHour(b.timeRange) || 0) - currentHour);
            return hourDistanceA - hourDistanceB;
          });
          [chosenSlot] = minSlots;
        }
      }
    } else {
      customTimeSlots.sort((a, b) => (getStartHour(a.timeRange) || 0) - (getStartHour(b.timeRange) || 0));
      [chosenSlot] = customTimeSlots;
    }
  }
  return chosenSlot;
};

const processHomeDeliverySlots = (
  homeDeliverySlots,
  homeDeliveryDateRangeSlots,
  regionCode,
  isFreeShippingApplicable,
  isHDExpressEnabled
) => {
  const currentLocaleTime = getLocaleTime(regionCode);
  const currentDate = new Date(currentLocaleTime).getDate();
  const currentHour = new Date(currentLocaleTime).getHours();
  const currentTime = new Date(currentLocaleTime).getTime();
  const nextDate = new Date(currentTime + 24 * 3600 * 1000).getDate();
  const result = [];
  let i;
  for (i = 0; i < homeDeliverySlots.length; i += 1) {
    const slot = homeDeliverySlots[i];
    const { customTimeSlots = [], slotDate } = slot;
    if (customTimeSlots.length === 0) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const [firstSlot] = customTimeSlots;
    const selectedSlot = isHDExpressEnabled ? getChosenSlot(customTimeSlots, currentHour) : firstSlot;
    const { cost, slotType, timeRange } = selectedSlot;
    const price = cost || undefined;
    const date = Number(slot.slotDate.date || 0);
    const isExpress = slotType.toLowerCase().includes('ex');
    const month = (slotDate.month || '').toLowerCase();
    if (date === currentDate && isExpress && isHDExpressEnabled) {
      result.push({
        title: 'Llega en 2 horas',
        price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
        isExpress: true,
        timeRange,
      });
    } else if (
      date === currentDate &&
      currentHour < 13 &&
      (isFreeShippingApplicable || price !== undefined) &&
      !isExpress
    ) {
      result.push({
        title: 'Hoy',
        price: isFreeShippingApplicable ? 'Gratis' : price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
      });
    } else if (
      date === currentDate &&
      currentHour >= 13 &&
      (isFreeShippingApplicable || price !== undefined) &&
      !isExpress
    ) {
      result.push({
        title: 'Mañana',
        price: isFreeShippingApplicable ? 'Gratis' : price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
      });
    } else if (date === nextDate && (isFreeShippingApplicable || price !== undefined)) {
      result.push({
        title: 'Mañana',
        price: isFreeShippingApplicable ? 'Gratis' : price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
      });
    } else if (date !== 0 && (isFreeShippingApplicable || price !== undefined) && month !== '') {
      result.push({
        title: `Desde el`,
        price: isFreeShippingApplicable ? 'Gratis' : price,
        date: pad(date),
        month: MONTHS[month],
        show: true,
        isDateRange: false,
      });
    }
  }

  if (homeDeliveryDateRangeSlots.length > 0) {
    const dateRangePrice = _.get(homeDeliveryDateRangeSlots, '[0].cost', undefined);

    const description = _.get(homeDeliveryDateRangeSlots, '[0].description', '');

    const dateRe = /[\d]+/g;
    const dateArray = description.match(dateRe) || [];
    let dateRangeDay = '';
    let dateRangeMonth = '';
    if (dateArray && Array.isArray(dateArray) && dateArray.length > 0) {
      [dateRangeDay, dateRangeMonth] = dateArray;
      dateRangeMonth = Number(dateRangeMonth);
    }

    if (isFreeShippingApplicable || dateRangePrice !== undefined) {
      result.push({
        title: 'Envío en rango de fechas',
        price: isFreeShippingApplicable ? 'Gratis' : dateRangePrice,
        date: dateRangeDay,
        month: dateRangeMonth,
        show: true,
        isDateRange: true,
      });
    }
  }

  // if (homeDeliverySlots.length > 0 && isFreeShippingApplicable) {
  //   result.push({
  //     title: 'Envío en fecha programada',
  //     price: 'Gratis',
  //     date: '',
  //     month: '',
  //     show: true,
  //     isDateRange: false,
  //   });
  //   return result;
  // }

  // const regex = /[\d.,]+/g;

  // const costArray = homeDeliverySlots.flatMap((slot) =>
  //   (slot.customTimeSlots || []).flatMap((timeSlot) => {
  //     const cost = timeSlot.cost || '';
  //     const normalisedCost = cost.match(regex);
  //     return normalisedCost && normalisedCost.length > 0
  //       ? { value: convertToNumber(normalisedCost[0]), original: cost }
  //       : [];
  //   })
  // );

  // if (costArray.length > 0) {
  //   const normalizedCosts = costArray.map((item) => item.value);

  //   // const minCost = Math.min(...normalizedCosts);
  //   // const maxCost = Math.max(...normalizedCosts);

  //   // const foundMinOriginal = costArray.find((cost) => cost.value === minCost);
  //   // const minOriginal = foundMinOriginal ? foundMinOriginal.original : costArray[0].original;

  //   // const foundMaxOriginal = costArray.find((cost) => cost.value === maxCost);
  //   // const maxOriginal = foundMaxOriginal ? foundMaxOriginal.original : costArray[0].original;

  //   // const reqCost = minCost === maxCost ? minOriginal : `${minOriginal}-${maxOriginal}`;

  //   // result.push({
  //   //   title: 'Envío en fecha programada',
  //   //   price: reqCost,
  //   //   date: '',
  //   //   month: '',
  //   //   show: true,
  //   //   isDateRange: false,
  //   // });
  // }

  return result;
};

const getHomeDeliverySlots = async ({ regionCode, homeDelivery, isFreeShippingApplicable, isHDExpressEnabled }) => {
  const homeDeliverySlots = _.get(homeDelivery, 'data.homeDelivery.deliverySlots', []);
  const homeDeliveryDateRangeSlots = _.get(homeDelivery, 'data.homeDeliveryDateRange.deliverySlots', []);
  if (
    (!homeDeliverySlots || homeDeliverySlots.length === 0) &&
    (!homeDeliveryDateRangeSlots || homeDeliveryDateRangeSlots.length === 0)
  ) {
    return [];
  }
  const result = processHomeDeliverySlots(
    homeDeliverySlots,
    homeDeliveryDateRangeSlots,
    regionCode,
    isFreeShippingApplicable,
    isHDExpressEnabled
  );
  if (result && Array.isArray(result) && result.length > 0) {
    return result;
  }
  return [];
};

const getHomeDeliveryData = async ({
  regionCode,
  comuna,
  isDefaultComuna,
  isFreeShippingApplicable,
  homeDelivery,
  setComunaName,
  setHomeDeliveryData,
  setParentStateName,
  setDeliveryError,
  setIsMapView,
  setSelectedDeliveryOption,
  labelsData,
  setTextGenerated,
  isHDExpressEnabled,
}) => {
  if (comuna && !_.isEmpty(comuna)) {
    const comunaName = _.get(comuna, 'name', '');
    const parentStateName = _.get(comuna, 'parentStateName', '');
    setParentStateName(parentStateName);
    const hdResult = await getHomeDeliverySlots({
      regionCode,
      isFreeShippingApplicable,
      homeDelivery,
      isHDExpressEnabled,
    });
    if (hdResult && Array.isArray(hdResult) && hdResult.length > 0) {
      setDeliveryError(AVAILABILITY_HOMEDELIVERY, false);
      setHomeDeliveryData(hdResult);
      setComunaName(comunaName);
      const {
        selectedType,
        price,
        date,
        month,
        storeName,
        storeId,
        isValid,
        locationName,
        politicalId,
        isDateRange,
        isExpress,
        timeRange,
      } = getSelectedData(hdResult, AVAILABILITY_HOMEDELIVERY, comuna, isDefaultComuna);
      if (isValid) {
        const selectionData = {
          type: selectedType,
          price,
          date,
          month,
          locationName,
          storeName,
          storeId,
          politicalId,
          isError: false,
          isDateRange,
          isExpress,
          timeRange,
          isFreeShippingApplicable,
          parentStateName,
          deliveryOptionType: AVAILABILITY_HOMEDELIVERY,
        };
        const stockText = generateDeliveryText(
          selectionData,
          AVAILABILITY_INSTOCK,
          labelsData,
          AVAILABILITY_HOMEDELIVERY
        );
        setTextGenerated((prev) => ({ ...prev, [AVAILABILITY_HOMEDELIVERY]: stockText }));
        setSelectedDeliveryOption(selectionData);
      }
    } else {
      setComunaName(comunaName);
      setHomeDeliveryData([]);
      setDeliveryError(AVAILABILITY_HOMEDELIVERY, true, 'NO_DELIVERY_SLOTS');
      setIsMapView(false);
      const selectionData = {
        selectedType: 'No disponible para',
        date: '',
        month: '',
        price: '',
        locationName: comunaName.toLowerCase(),
        storeName: '',
        storeId: '',
        isError: true,
        isFreeShippingApplicable: false,
        parentStateName,
        deliveryOptionType: AVAILABILITY_HOMEDELIVERY,
      };
      const stockText = generateDeliveryText(
        selectionData,
        AVAILABILITY_OUTOFSTOCK,
        labelsData,
        AVAILABILITY_HOMEDELIVERY
      );
      setTextGenerated((prev) => ({ ...prev, [AVAILABILITY_HOMEDELIVERY]: stockText }));
      setSelectedDeliveryOption(selectionData);
    }
  }
};

export { getHomeDeliveryData, getHomeDeliverySlots, processHomeDeliverySlots };
